// src/services/orderService.js
// All order operations go through the Express backend → MongoDB
// Backend: https://panch-tatva-services.onrender.com (prod) | http://localhost:4000 (dev)

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const authFetch = async (url, idToken, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Request failed: ${res.status}`);
  }
  return res.json();
};

/**
 * Create a new order after payment is verified.
 * POST /api/orders
 *
 * @param {Object} orderData - { razorpayOrderId, razorpayPaymentId, razorpaySignature,
 *                               items, pricing, shippingAddress, shippingMethod }
 * @param {string} idToken   - Firebase ID token
 * @returns {Object}         - Created order document (includes _id)
 */
export const createOrder = async (orderData, idToken) => {
  const data = await authFetch(`${API_URL}/api/orders`, idToken, {
    method: 'POST',
    body: JSON.stringify(orderData),
  });
  return { ...data, id: data._id || data.id };
};

/**
 * Get all orders for the logged-in user.
 * GET /api/orders
 * Optional query params: status, limit, skip
 *
 * @param {string} userId  - Unused (backend uses token), kept for compat
 * @param {string} idToken - Firebase ID token
 * @returns {Array}
 */
export const getOrdersByUser = async (userId, idToken) => {
  const data = await authFetch(`${API_URL}/api/orders`, idToken);
  const orders = Array.isArray(data) ? data : data.orders || [];
  return orders.map((o) => ({
    ...o,
    id: o._id || o.id,
    createdAt: o.createdAt ? new Date(o.createdAt) : new Date(),
    updatedAt: o.updatedAt ? new Date(o.updatedAt) : new Date(),
  }));
};

/**
 * Get a single order by its MongoDB _id.
 * GET /api/orders/:orderId
 *
 * @param {string} orderId - MongoDB document _id
 * @param {string} idToken - Firebase ID token
 * @returns {Object|null}
 */
export const getOrderById = async (orderId, idToken) => {
  try {
    const data = await authFetch(`${API_URL}/api/orders/${orderId}`, idToken);
    return {
      ...data,
      id: data._id || data.id,
      createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date(),
    };
  } catch (err) {
    if (err.message?.includes('404') || err.message?.includes('not found')) return null;
    throw err;
  }
};

/**
 * Update an order's delivery status.
 * PATCH /api/orders/:orderId/status
 * Valid: pending | confirmed | shipped | out-for-delivery | delivered | cancelled | payment-failed
 *
 * @param {string} orderId - MongoDB document _id
 * @param {string} status  - New status value
 * @param {string} idToken - Firebase ID token
 * @returns {Object}       - { status, message }
 */
export const updateOrderStatus = async (orderId, status, idToken) => {
  return authFetch(`${API_URL}/api/orders/${orderId}/status`, idToken, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
};
